import { NavLink, Redirect, useHistory, useRouteMatch } from 'react-router-dom'
import { Button, Confirm, Loader, Placeholder, Popup } from 'semantic-ui-react'
import useSWRInfinite from 'swr/infinite'
import { AxiosError } from 'axios'
import { Helmet } from 'react-helmet'
import * as React from 'react'
import useSWR from 'swr'
import { z } from 'zod'

import { candidateSchema, EntityList, entityListSchema } from '../../types'
import { classNames, validateResponse } from '../../utils'
import useKeyboardShortcut from '../../hooks/useKeyboardShortcut'
import SearchResultCard from '../../components/candidates/SearchResultCard'
import CandidatePreview from '../../components/candidates/CandidatePreview'
import EditableValue from '../../components/recipes/ui/EditableValue'
import { useToasts } from '../../components/toasts/ToastsProvider'
import PageLoader from '../../components/pageLoader'
import { useApi } from '../../store/mainContext'

type RouteParams = { listId?: string }

type Dialog = { type: 'delete-list'; list: EntityList }

const ITEMS_PER_PAGE = 20

export default function CandidatesListsPage() {
  const { params } = useRouteMatch<RouteParams>()
  const { listId } = params

  const { addToast } = useToasts()
  const history = useHistory()
  const api = useApi()

  const [selectedCandidateId, setSelectedCandidateId] = React.useState<string>()
  const [currentDialog, setCurrentDialog] = React.useState<Dialog | null>(null)

  const [isDeleting, setIsDeleting] = React.useState(false)

  const lists = useSWR(['lists', 'candidates'], () =>
    api
      .get('/lists', { params: { collection: 'candidates' } })
      .then(validateResponse(z.array(entityListSchema))),
  )

  const [total, setTotal] = React.useState<number>()

  const members = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData && !previousPageData.length) return null // reached the end
      return listId ? [pageIndex, 'lists', 'candidates', listId] : null
    },
    ([page]) =>
      api
        .get('/candidates/in', {
          params: { lists: listId },
          headers: {
            'astor-limit': ITEMS_PER_PAGE,
            'astor-skip': ITEMS_PER_PAGE * page,
          },
        })
        .then((response) => {
          setTotal(parseInt(response.headers['astor-total'], 10))
          return validateResponse(z.array(candidateSchema))(response)
        }),
    // .then(validateResponse(z.array(candidateSchema))),
    {
      // pages are independent so we can revalidate them in parallel
      parallel: true,
      // select first item in page if no selection or selection not visible in page
      onSuccess(data) {
        const fMembers = data.flat()
        if (
          fMembers[0] &&
          !fMembers.find((c) => c._id === selectedCandidateId)
        ) {
          setSelectedCandidateId(fMembers[0]._id)
        }
      },
    },
  )

  const flattenedMembers = (members.data || []).flat()

  useKeyboardShortcut(
    () => {
      if (!flattenedMembers) return
      const currentIndex = flattenedMembers.findIndex(
        (c) => c._id === selectedCandidateId,
      )
      // TODO no pagination at the moment
      if (currentIndex === flattenedMembers.length - 1) {
        members.setSize(members.size + 1)
      }
      const nextCandidate =
        flattenedMembers[
          Math.min(currentIndex + 1, flattenedMembers.length - 1)
        ]
      if (nextCandidate) setSelectedCandidateId(nextCandidate._id)
    },
    { code: 'ArrowDown', shiftKey: true },
  )

  useKeyboardShortcut(
    () => {
      if (!flattenedMembers) return
      const prevCandidate =
        flattenedMembers[
          Math.max(
            flattenedMembers.findIndex((c) => c._id === selectedCandidateId) -
              1,
            0,
          )
        ]
      if (prevCandidate) setSelectedCandidateId(prevCandidate._id)
    },
    { code: 'ArrowUp', shiftKey: true },
  )

  // Force selected list item into view when selection changes.
  // Required because keyboard navigation might select an item that is hidden.
  React.useEffect(() => {
    if (!selectedCandidateId) return
    const el = document.querySelector(
      `[data-scrollintoview="${selectedCandidateId}"]`,
    )
    if (!el) return
    const { top, bottom } = el.getBoundingClientRect()
    if (top >= 0 && bottom <= window.innerHeight) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [selectedCandidateId])

  const selectedCandidate = flattenedMembers.find(
    (candidate) => candidate._id === selectedCandidateId,
  )

  const selectedList = lists.data?.find((list) => list._id === listId)

  const handleUpdateList = async (values: Partial<EntityList>) => {
    await api.patch<EntityList>(`lists/${selectedList?._id}`, values)
    lists.mutate()
  }

  const [isCreating, setIsCreating] = React.useState(false)

  const handleAddList = async () => {
    setIsCreating(true)
    return api
      .post('lists', {
        name: 'New List',
        description: 'Just another list.',
        collection: 'candidates',
      })
      .then(validateResponse(entityListSchema))
      .then(({ _id }) =>
        lists.mutate().then(() => {
          history.push(`/candidates/lists/${_id}`)
        }),
      )
      .catch((err: AxiosError) =>
        addToast(err.response?.data.message || err.message, {
          variant: 'danger',
        }),
      )
      .finally(() => setIsCreating(false))
  }

  if (lists.isLoading) return <PageLoader />

  if (!lists.data) {
    addToast('Lists not found', { variant: 'danger' })
    return <Redirect to="/candidates" />
  }

  // If in root "/lists" URL, automatically navigate to first list (if one exists)
  const maybeFirstListId = lists.data[0]?._id
  if (!params.listId && maybeFirstListId) {
    return <Redirect to={`/candidates/lists/${maybeFirstListId}`} />
  }

  const handleDeleteList = async (id: string) => {
    setIsDeleting(true)
    return api
      .delete('/lists/' + id)
      .then(() => lists.mutate())
      .then(() => {
        if (maybeFirstListId) {
          history.push(`/candidates/lists/${maybeFirstListId}`)
        }
        setCurrentDialog(null)
      })
      .catch((err: AxiosError) =>
        addToast(err.response?.data.message || err.message, {
          variant: 'danger',
        }),
      )
      .finally(() => setIsDeleting(false))
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Candidates Lists</title>
      </Helmet>

      <div className="flex h-screen">
        {/* LISTS LIST */}
        <div className="w-[260px] shrink-0 overflow-y-auto border-r border-dotted border-border-grey [&::-webkit-scrollbar]:hidden">
          <header className="sticky top-0 flex items-baseline justify-between gap-4 border-b bg-white px-3 py-3">
            <span className="text-sm font-semibold uppercase leading-[29px] text-slate-500">
              Lists{' '}
              <span className="ml-0.5 font-normal text-slate-400">
                ({lists.data.length.toString()})
              </span>
            </span>

            <Popup
              content="Create List"
              position="left center"
              trigger={
                <Button
                  loading={isCreating}
                  disabled={isCreating}
                  onClick={handleAddList}
                  icon="plus"
                  basic
                  size="mini"
                  className="!mr-0"
                />
              }
            />
          </header>

          {lists.data.length > 0 ? (
            <ul>
              {lists.data.map((list) => (
                <li
                  className="cursor-pointer border-b border-dotted border-border-grey"
                  key={list._id}
                >
                  <NavLink
                    to={`/candidates/lists/${list._id}`}
                    className="block w-full bg-white px-3 py-4 text-left !text-black  hover:bg-slate-50"
                    activeClassName="!bg-primary/5"
                  >
                    <div className="font-semibold leading-snug">
                      {list.name || (
                        <span className="italic text-slate-400">No name</span>
                      )}
                    </div>
                    <div
                      className={classNames(
                        'text-sm',
                        list.description
                          ? 'text-slate-500'
                          : 'italic text-slate-400',
                      )}
                    >
                      {list.description || 'No description'}
                    </div>
                  </NavLink>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-8 text-center text-slate-500">
              Add your first list!
            </div>
          )}
        </div>

        {/* LIST MEMBERS */}
        {selectedList ? (
          <div className="relative min-h-screen w-[450px] shrink-0 overflow-y-auto border-r border-dotted border-border-grey [&::-webkit-scrollbar]:hidden">
            <div className="min-h-screen">
              <header className="flex items-start gap-4 border-b border-dotted border-border-grey px-4 py-4">
                <div className="-mt-2.5 grow">
                  <EditableValue
                    type="input"
                    className="min-w-[300px] max-w-[400px] text-3xl"
                    value={selectedList.name}
                    onSave={(value) => handleUpdateList({ name: value })}
                  />
                  <EditableValue
                    type="input"
                    className="mt-1 min-w-[300px] max-w-[400px] py-1 leading-snug text-gray-500"
                    value={selectedList.description || 'Just a list.'}
                    onSave={(value) => handleUpdateList({ description: value })}
                  />
                </div>
                <Popup
                  content="Delete List"
                  position="top center"
                  trigger={
                    <Button
                      className="!mr-0"
                      onClick={() =>
                        setCurrentDialog({
                          type: 'delete-list',
                          list: selectedList,
                        })
                      }
                      type="button"
                      icon="trash alternate outline"
                      size="mini"
                      color="red"
                      basic
                    />
                  }
                />
              </header>
              {members.isLoading ? (
                <ul>
                  <li className="border-b px-5 py-6">
                    <Placeholder>
                      <Placeholder.Header>
                        <Placeholder.Line length="short" />
                      </Placeholder.Header>
                      <Placeholder.Paragraph>
                        <Placeholder.Line length="full" />
                        <Placeholder.Line length="short" />
                        <Placeholder.Line length="medium" />
                      </Placeholder.Paragraph>
                    </Placeholder>
                  </li>
                  <li className="border-b px-5 py-6">
                    <Placeholder>
                      <Placeholder.Header>
                        <Placeholder.Line length="short" />
                      </Placeholder.Header>
                      <Placeholder.Paragraph>
                        <Placeholder.Line length="medium" />
                        <Placeholder.Line length="short" />
                        <Placeholder.Line length="full" />
                      </Placeholder.Paragraph>
                    </Placeholder>
                  </li>
                  <li className="border-b px-5 py-6">
                    <Placeholder>
                      <Placeholder.Header>
                        <Placeholder.Line length="short" />
                      </Placeholder.Header>
                      <Placeholder.Paragraph>
                        <Placeholder.Line length="medium" />
                        <Placeholder.Line length="short" />
                        <Placeholder.Line length="full" />
                      </Placeholder.Paragraph>
                    </Placeholder>
                  </li>
                </ul>
              ) : (
                <>
                  {flattenedMembers.length > 0 ? (
                    <>
                      {flattenedMembers.map((candidate) => {
                        return (
                          <div
                            className={classNames(
                              'cursor-pointer border-b border-dotted border-border-grey px-5 py-5 hover:bg-gray-50',
                              selectedCandidateId === candidate._id &&
                                'border-r-primary !bg-primary/5',
                            )}
                            onClick={() => {
                              setSelectedCandidateId(candidate._id)
                            }}
                            key={candidate._id}
                            data-scrollintoview={candidate._id}
                          >
                            <SearchResultCard
                              candidate={candidate}
                              mutate={members.mutate}
                            />
                          </div>
                        )
                      })}
                    </>
                  ) : (
                    <div className="mt-16 text-center">
                      <div className="mb-2 text-slate-600">
                        The list is empty
                      </div>
                      <NavLink to="/candidates/search">Add candidates!</NavLink>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="sticky bottom-0 left-0 right-0 flex items-baseline gap-2 border-r border-t bg-white px-5 py-2">
              <div className="grow text-base">
                Showing <strong>{flattenedMembers.length}</strong> of{' '}
                <strong>{total?.toString()}</strong>{' '}
                {total === 1 ? 'item' : 'items'}
              </div>
              <Button
                onClick={() => members.setSize(members.size + 1)}
                disabled={members.isValidating}
                className={classNames(
                  '!mr-0',
                  flattenedMembers.length >= (total || 0) && 'opacity-0',
                )}
                type="button"
                compact
                basic
              >
                Show more...
              </Button>
            </div>
          </div>
        ) : (
          <div className="w-full self-center p-4 text-center text-slate-500">
            Select a list to view candidates assigned to it.
          </div>
        )}

        {/* CANDIDATE PREVIEW */}
        {selectedCandidate ? (
          <CandidatePreview selectedCandidate={selectedCandidate} />
        ) : members.isLoading ? (
          <div className="mx-auto flex items-center">
            <Loader inline active />
          </div>
        ) : (
          <div className="w-full self-center p-4 text-center text-slate-500">
            Pick a candidate to see his/her profile.{' '}
          </div>
        )}
      </div>

      {currentDialog?.type === 'delete-list' && (
        <Confirm
          header="Hey... deleting the list?"
          content={
            <div className="-mt-1 mb-2 px-6 text-base">
              This action will also unlink all the list candidates (if any).
            </div>
          }
          closeOnDimmerClick={!isDeleting}
          closeOnEscape={!isDeleting}
          onConfirm={() => handleDeleteList(currentDialog.list._id)}
          onCancel={() => setCurrentDialog(null)}
          onClose={() => setCurrentDialog(null)}
          cancelButton={<Button basic disabled={isDeleting} content="Cancel" />}
          confirmButton={
            <Button
              disabled={isDeleting}
              loading={isDeleting}
              content="Sure, delete"
              primary={false}
              color="red"
            />
          }
          size="tiny"
          open
        />
      )}
    </React.Fragment>
  )
}
